import React from 'react';
import { navigate } from 'gatsby';
import { formatCurrency } from '../utils/price';

import DocumentIcon from '../assets/icons/pdf2.svg';
import WordDocumentIcon from '../assets/icons/word-icon.png';
import PowerPointIcon from '../assets/icons/pptx-icon-no-bg.png';
import LockIcon from '../assets/icons/lock.svg';
import LockSubIcon from '../assets/icons/sub-lock.svg';
import UnlockSubIcon from '../assets/icons/open-sub-lock.svg';
import YoutubeLogo from '../assets/images/youtube_logo.png';
import { slugify } from '@src/utils/slugify';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

const SPACES_URL = 'https://mymathscloud.ams3.digitaloceanspaces.com/';

const isBrowser = typeof window !== 'undefined';

const DOCUMENT_TYPE_MAP = {
  word: { icon: WordDocumentIcon, label: 'Word' },
  pptx: { icon: PowerPointIcon, label: 'PowerPoint' },
  pdf: { icon: DocumentIcon, label: 'PDF' },
} as const;

type DocType = keyof typeof DOCUMENT_TYPE_MAP;

function getDocType(fileUrl: string): DocType {
  if (fileUrl.endsWith('.docx')) return 'word';
  if (fileUrl.endsWith('.pptx')) return 'pptx';
  return 'pdf';
}

function getFileUrl(rawUrl: string): string {
  return rawUrl.includes(SPACES_URL) ? unescape(rawUrl.replace(SPACES_URL, '')) : rawUrl;
}

type DocumentLinkProps = {
  document: {
    id: string;
    originalId?: string;
    name?: string;
    displayName?: string;
    fileUrl: string;
    externalUrl?: string;
    price?: number;
    hasPassword?: boolean;
    freeDocument?: boolean;
    freeSample?: boolean;
    isSubscription?: boolean;
    videoSolution?: string;
    module: {
      slug: string;
    };
    documentCategory: { slug: string }[];
    videoGuide: {
      url: string;
    };
  };
  isSubscribed: boolean;
  fallbackPrice: string | number;
  action?: React.ReactNode;
};

export const DocumentLink: React.FC<DocumentLinkProps> = ({
  document,
  isSubscribed,
  fallbackPrice,
  action,
}) => {
  console.log({ document });

  const fileUrl = getFileUrl(document.fileUrl);
  const docType = getDocType(fileUrl);
  const { icon, label } = DOCUMENT_TYPE_MAP[docType];

  const downloadUrl = `${process.env.GATSBY_BACKEND_URL}/api/download/modules/${fileUrl}?id=${document.id}`;

  // const href = document.externalUrl
  //   ? document.externalUrl
  //   : document.isSubscription && isSubscribed
  //   ? `/embeded?id=${document.id}`
  //   : document.isSubscription
  //   ? ''
  //   : downloadUrl;

  const module = document?.module?.slug;

  const docUrl =
    document.isSubscription && isSubscribed
      ? document?.videoGuide?.url
        ? `/embeded?id=${document.id}&videoGuide=${encodeURI(document?.videoGuide?.url)}`
        : `/embeded?id=${document.id}`
      : `/modules/${module}/${document.documentCategory
          ?.map((c) => c.slug)
          .join('/')}/${encodeURIComponent(slugify(document.name))}`;

  const href = document.freeDocument || document.freeSample ? downloadUrl : docUrl;

  const handleClick = () => {
    if (isBrowser && document.hasPassword) {
      localStorage.setItem('open-modal-on-load', 'true');
    }

    if (document.isSubscription) {
      if (isSubscribed) {
        const destinationUrl = `/embeded?id=${document.id}`;
        trackDocumentClick(document, {
          source: 'legacy_link',
          outcome: 'opened',
          isSubscribed,
          destinationUrl,
        });
        window.location.href = destinationUrl;
      } else {
        trackDocumentClick(document, {
          source: 'legacy_link',
          outcome: 'detail',
          isSubscribed,
          destinationUrl: href,
        });
        window.location.href = href;
      }
    } else {
      trackDocumentClick(document, {
        source: 'legacy_link',
        outcome: 'opened',
        isSubscribed,
        destinationUrl: downloadUrl,
      });
      window.location.href = downloadUrl;
    }
  };

  return (
    <li className="category-option">
      <img src={icon} className="category-option-image" alt="Category option" />

      <div className="category-option-text">
        <a
          href={href}
          onClick={handleClick}
          className="category-option-link button-text cursor-pointer"
        >
          {document.displayName || document.name}
        </a>
        <span className="category-option-info lh-1.5">
          {document.freeSample && <span className="badge free-sample">FREE SAMPLE</span>}
          {!document.freeSample && !document.freeDocument && document.hasPassword && (
            <span className="badge premium">
              £{document.price || formatCurrency.format(fallbackPrice)}
            </span>
          )}
          {label}
          {document.videoSolution && (
            <span className="ml-0.5">
              <a href={document.videoSolution} target="_blank" style={{ fontSize: '1rem' }}>
                Video solution
              </a>
              <img src={YoutubeLogo} style={{ width: '2rem', marginLeft: '0.5rem' }} />
            </span>
          )}
        </span>
      </div>

      {document.isSubscription && isSubscribed ? (
        <img src={UnlockSubIcon} className="lock-icon" alt="Unlock" />
      ) : document.isSubscription ? (
        <img
          src={LockSubIcon}
          className="lock-icon"
          alt="Locked with sub content"
          onClick={() => {
            trackDocumentClick(document, {
              source: 'legacy_link',
              outcome: 'subscribe',
              isSubscribed,
              destinationUrl: '/subscriptions',
            });
            navigate('/subscriptions');
          }}
        />
      ) : document.hasPassword ? (
        <img src={LockIcon} className="lock-icon" alt="Locked" />
      ) : null}

      {action}
    </li>
  );
};
